import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { InputRef } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  AppButton,
  AppCard,
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppInputNumber,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import VisitErrorMessageList from '@/components/molecules/VisitErrorMessageList';
import { RA2NDatePicker } from '@/components/organisms';
import { WEEKDAY_LABELS } from '@/constants/weekday';
import type { PatientFormData } from '@/pages/main/patient/types/patientTypes';
import type { PatientAddress } from '@/pages/main/patientAddresses/types';
import type { VisitScheduleEvent } from '@/pages/main/visitSchedule/types';
import { parseDate } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import {
  CONSULTATION_TYPE,
  DATEPICKER_FORMAT,
  DISTANCE_WARNING_MSG,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from './VisitScheduleModal.constants';
import { formatPatientAddressLabel, getAddressLabel } from './VisitScheduleModal.helpers';

type SelectOption<T extends string | number> = {
  value: T;
  label: string;
};

type PatientAddressInfo = {
  address_name?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  is_special_area_addition?: boolean;
};

interface VisitScheduleModalFormProps {
  visit?: VisitScheduleEvent | null;
  tenant: string;
  checkinEnabled: boolean;
  visitDate: Dayjs;
  setVisitDate: Dispatch<SetStateAction<Dayjs>>;
  visitHour: string;
  setVisitHour: Dispatch<SetStateAction<string>>;
  visitMinute: string;
  setVisitMinute: Dispatch<SetStateAction<string>>;
  patientSearchInputRef: RefObject<InputRef>;
  patientSearchKeyword: string;
  setPatientSearchKeyword: Dispatch<SetStateAction<string>>;
  handleSearchClick: () => void;
  isSearching: boolean;
  patientId: number | undefined;
  selectedPatient: PatientFormData | undefined;
  patientSelectOptions: SelectOption<number>[];
  handlePatientSelectChange: (value: number | undefined) => void;
  showSuddenlyVisitArea: boolean;
  isSuddenlyVisit: boolean;
  setIsSuddenlyVisit: Dispatch<SetStateAction<boolean>>;
  hospitalName: string;
  setHospitalName: Dispatch<SetStateAction<string>>;
  doctorName: string;
  setDoctorName: Dispatch<SetStateAction<string>>;
  visitReason: string;
  setVisitReason: Dispatch<SetStateAction<string>>;
  alignmentDate: string;
  setAlignmentDate: Dispatch<SetStateAction<string>>;
  patientAddressName: string;
  setPatientAddressName: Dispatch<SetStateAction<string>>;
  addressSelectOptions: SelectOption<string>[];
  showDistanceWarning: boolean;
  addressList: PatientAddress[];
  baseInfoAtVisitDate: (Record<string, unknown> & PatientAddressInfo) | undefined;
  visitMinutes: number | null;
  setVisitMinutes: Dispatch<SetStateAction<number | null>>;
  showNoElectricCheckboxResolved: boolean;
  isElectric: boolean;
  /** 令和8年法改正 No 5 — hidden for treatments before 2026-07-01 (the 加算 did not exist). */
  setIsElectric: Dispatch<SetStateAction<boolean>>;
  isR2: boolean;
  consultationType: string;
  setConsultationType: Dispatch<SetStateAction<string>>;
  copyTargetWeekdays: number[];
  setCopyTargetWeekdays: Dispatch<SetStateAction<number[]>>;
  treatmentMemo: string;
  setTreatmentMemo: Dispatch<SetStateAction<string>>;
}

export function VisitScheduleModalForm({
  visit,
  tenant,
  checkinEnabled,
  visitDate,
  setVisitDate,
  visitHour,
  setVisitHour,
  visitMinute,
  setVisitMinute,
  patientSearchInputRef,
  patientSearchKeyword,
  setPatientSearchKeyword,
  handleSearchClick,
  isSearching,
  patientId,
  selectedPatient,
  patientSelectOptions,
  handlePatientSelectChange,
  showSuddenlyVisitArea,
  isSuddenlyVisit,
  setIsSuddenlyVisit,
  hospitalName,
  setHospitalName,
  doctorName,
  setDoctorName,
  visitReason,
  setVisitReason,
  alignmentDate,
  setAlignmentDate,
  patientAddressName,
  setPatientAddressName,
  addressSelectOptions,
  showDistanceWarning,
  addressList,
  baseInfoAtVisitDate,
  visitMinutes,
  setVisitMinutes,
  showNoElectricCheckboxResolved,
  isElectric,
  setIsElectric,
  isR2,
  consultationType,
  setConsultationType,
  copyTargetWeekdays,
  setCopyTargetWeekdays,
  treatmentMemo,
  setTreatmentMemo,
}: VisitScheduleModalFormProps) {
  return (
    <div className="flex-1 min-h-0 px-4 pt-4 pb-2 overflow-y-auto">
      <AppForm layout="vertical">
        <AppCard className="shadow-md" styles={{ body: { padding: 16 } }}>
          <div className="space-y-4">
            {/* 1. 訪問日時 - date + time picker */}
            <div>
              <AppFormLabel label="訪問日時" />
              <div className="flex flex-wrap items-center gap-2">
                <RA2NDatePicker
                  value={visitDate.isValid() ? visitDate.format('YYYY-MM-DD') : ''}
                  placeholder="元号00年00月00日"
                  onChange={date => {
                    if (!date) {
                      setVisitDate(dayjs(''));
                      return;
                    }
                    const parsed = parseDate(date, DATEPICKER_FORMAT);
                    const parsedDayjs = dayjs(parsed);
                    if (!parsedDayjs.isValid()) {
                      setVisitDate(dayjs(''));
                      return;
                    }
                    setVisitDate(parsedDayjs);
                  }}
                  className="min-w-[140px]"
                />
                <AppSelect
                  value={visitHour}
                  onChange={value => setVisitHour(value != null ? String(value) : '')}
                  options={HOUR_OPTIONS}
                  className="w-14"
                  allowClear={false}
                />
                <span className="text-gray-600">時</span>
                <AppSelect
                  value={visitMinute}
                  onChange={value => setVisitMinute(value != null ? String(value) : '')}
                  options={MINUTE_OPTIONS}
                  className="w-14"
                  allowClear={false}
                />
                <span className="text-gray-600">分</span>
              </div>
            </div>

            {/* 2. 受診者名 - input + 検索 button */}
            <div>
              <AppFormLabel label="受診者名" />
              <div className="flex gap-2">
                <AppTextField
                  ref={patientSearchInputRef}
                  value={patientSearchKeyword}
                  onChange={event => setPatientSearchKeyword(event.target.value)}
                  placeholder="受診者名で検索"
                  className="flex-1"
                  onPressEnter={handleSearchClick}
                  maxLength={255}
                />
                <AppButton
                  type="primary"
                  onClick={handleSearchClick}
                  loading={isSearching}
                  icon={<i className="fa fa-search" />}
                >
                  検索
                </AppButton>
              </div>
            </div>

            {/* 3. 受診者 - RA2: readonly = serial_number, Select = patient (value = patient_id) */}
            <div>
              <AppFormLabel label="受診者" />
              <div className="flex items-center min-w-0 gap-2 overflow-hidden">
                <AppTextField
                  value={patientId != null ? String(selectedPatient?.serial_number ?? patientId) : ''}
                  disabled
                  placeholder="—"
                  className="w-20"
                />
                <AppSelect
                  value={patientId != null ? patientId : undefined}
                  onChange={value =>
                    handlePatientSelectChange(value != null ? Number(value) : undefined)
                  }
                  options={patientSelectOptions}
                  className="flex-1 min-w-0"
                  allowClear={false}
                />
              </div>
            </div>

            {/* 6b. 突発的な往療 (RA2: 202406法改正) - only shown when patient/visit meets conditions */}
            {showSuddenlyVisitArea && (
              <>
                <div>
                  <AppCheckbox
                    checked={isSuddenlyVisit}
                    onChange={event => {
                      setIsSuddenlyVisit(event.target.checked);
                    }}
                  >
                    突発的な往療
                  </AppCheckbox>
                </div>
                {isSuddenlyVisit && (
                  <>
                    <div>
                      <AppFormLabel label="医療機関名" required />
                      <AppTextField
                        value={hospitalName}
                        onChange={event => setHospitalName(event.target.value)}
                        placeholder="医療機関名"
                        maxLength={50}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <AppFormLabel label="医師名" required />
                      <AppTextField
                        value={doctorName}
                        onChange={event => setDoctorName(event.target.value)}
                        placeholder="医師名"
                        maxLength={50}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <AppFormLabel label="突発往療理由" required />
                      <AppTextField
                        value={visitReason}
                        onChange={event => setVisitReason(event.target.value)}
                        placeholder="突発往療理由"
                        maxLength={50}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <AppFormLabel label="連携日" required />
                      <RA2NDatePicker
                        value={alignmentDate || ''}
                        placeholder="元号00年00月00日"
                        onChange={date => {
                          if (!date) {
                            setAlignmentDate('');
                            return;
                          }
                          const parsed = parseDate(date, DATEPICKER_FORMAT);
                          const parsedDayjs = dayjs(parsed);
                          if (!parsedDayjs.isValid()) {
                            setAlignmentDate('');
                            return;
                          }
                          setAlignmentDate(parsedDayjs.format('YYYY-MM-DD'));
                        }}
                        className="w-full"
                      />
                      <p className="mt-1 text-sm text-red-600">
                        同意書交付の医師以外の場合、その理由をレセプト摘要欄に手書きで記載ください。
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* 4. 受診者住所 - select like old system, options from selected patient (no separate API call) */}
            <div>
              <AppFormLabel label="受診者住所" />
              <AppSelect
                value={patientAddressName?.trim() || undefined}
                onChange={value => setPatientAddressName(value != null ? String(value) : '')}
                options={addressSelectOptions}
                className="w-full"
                allowClear={false}
              />
              {showDistanceWarning && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {DISTANCE_WARNING_MSG}
                </p>
              )}
              {/* BD: 特別地域加算 - patient_addresses OR patient_informations base may set this flag */}
              {(() => {
                const selectedLabel = (patientAddressName ?? '').trim();
                const selectedAddr = addressList.find(
                  address => formatPatientAddressLabel(address) === selectedLabel
                );
                const baseLabel = getAddressLabel(baseInfoAtVisitDate);
                const isBaseSelected = !!baseLabel && baseLabel === selectedLabel;
                const baseHasFlag =
                  isBaseSelected && !!baseInfoAtVisitDate?.is_special_area_addition;
                return selectedAddr?.is_special_area_addition || baseHasFlag ? (
                  <p className="mt-1 text-sm font-medium text-red-600">特別地域加算</p>
                ) : null;
              })()}
            </div>

            {/* 5. 所要時間（分） + 電療 side by side (same as old system) */}
            <div>
              <AppFormLabel label="所要時間" />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-2">
                  <AppInputNumber
                    min={1}
                    max={999}
                    value={visitMinutes ?? undefined}
                    onChange={value => setVisitMinutes(typeof value === 'number' ? value : null)}
                    className="w-24"
                  />
                  <span className="text-gray-600">分</span>
                </div>
                {/* 電療: only shown when selected patient has electric_treatment_is / hot_treatment_is */}
                {showNoElectricCheckboxResolved && (
                  <AppCheckbox
                    checked={isElectric}
                    onChange={event => setIsElectric(event.target.checked)}
                  >
                    {isR2 ? '温罨法・電療' : '電療'}
                  </AppCheckbox>
                )}
              </div>
            </div>

            {/* 6. 診療区分 (RA2: insurance_type_id=4 → forced 保険診療, hide radio) */}
            <div>
              <AppFormLabel label="診療区分" />
              {selectedPatient?.insurance_type_id === 4 ||
              selectedPatient?.insurance_type_id === '4' ? (
                <span className="text-gray-700">{CONSULTATION_TYPE}</span>
              ) : (
                <AppRadioGroup
                  value={consultationType}
                  onChange={event => setConsultationType(event.target.value)}
                >
                  <AppRadio value={CONSULTATION_TYPE}>{CONSULTATION_TYPE}</AppRadio>
                </AppRadioGroup>
              )}
            </div>

            {/* 7. コピー対象 — shown in both create and edit modes. */}
            <div>
              <AppFormLabel label="コピー対象" />
              <div className="flex flex-wrap gap-4">
                {WEEKDAY_LABELS.map(({ value, label }) => {
                  const isCurrentDay = value === visitDate.day();
                  return (
                    <AppCheckbox
                      key={value}
                      disabled={isSuddenlyVisit || isCurrentDay}
                      checked={isCurrentDay || copyTargetWeekdays.includes(value)}
                      onChange={event => {
                        if (event.target.checked) {
                          setCopyTargetWeekdays(prev => [...prev, value]);
                        } else {
                          setCopyTargetWeekdays(prev => prev.filter(x => x !== value));
                        }
                      }}
                    >
                      {label}
                    </AppCheckbox>
                  );
                })}
              </div>
            </div>
          </div>
        </AppCard>
        <div className="mt-4 space-y-4">
          {/* submit-total validation errors (VisitErrorChecker) */}
          <VisitErrorMessageList
            errorItems={visit?.error_items}
            errorMessages={visit?.error_messages}
            tenant={tenant}
          />

          {/* 8. 訪問実績 (BD: shown whenever clinic has check-in enabled; values blank when not yet checked in/out). */}
          {checkinEnabled && (
            <div>
              <span className="text-sm text-gray-600">訪問実績</span>
              <p className="text-sm text-gray-700 mt-0.5">
                {visit?.massager_started_at != null && (
                  <span>{dayjs(visit.massager_started_at).format('YYYY/MM/DD HH:mm')}</span>
                )}
                <span> から </span>
                {visit?.massager_ended_at != null && (
                  <span>{dayjs(visit.massager_ended_at).format('YYYY/MM/DD HH:mm')}</span>
                )}
              </p>
            </div>
          )}

          {/* 9. 施術メモ */}
          <div>
            <AppFormLabel label="施術メモ" />
            <AppTextArea
              value={treatmentMemo}
              onChange={event => setTreatmentMemo(event.target.value)}
              placeholder="施術メモ"
              rows={3}
              maxLength={255}
              className="w-full"
            />
          </div>
        </div>
      </AppForm>
    </div>
  );
}
